<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <h2 class="text-white font-weight-bold mb-1">{{ $t('maker.title') }}</h2>
          <p class="text-muted small mb-0">{{ $t('maker.subtitle') }}</p>
        </div>
        <div class="mt-2 mt-md-0 d-flex align-items-center gap-2 flex-wrap">
          <span class="badge badge-warning text-dark px-3 py-2 font-weight-bold mr-2">
            <i class="mdi mdi-shield-account mr-1"></i>
            {{ maker.length }} {{ $t('nav.gameMaker') }}
          </span>
          <router-link :to="{ name: 'MakerStudio' }" class="btn btn-outline-info px-3 py-2 font-weight-bold">
            <i class="mdi mdi-hammer-wrench mr-1"></i> {{ $t('nav.makerStudio') }}
          </router-link>
          <router-link :to="{ name: 'CreateMaker' }" class="btn btn-primary px-3 py-2 font-weight-bold">
            <i class="mdi mdi-plus mr-1"></i> {{ $t('maker.insertNew') }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- Maker Role Info Banner -->
    <div class="col-12 mb-4">
      <div class="card card-glass p-3 p-md-4" style="background: linear-gradient(135deg, rgba(26, 21, 38, 0.95) 0%, rgba(15, 14, 25, 0.95) 100%); border: 1px solid rgba(245, 158, 11, 0.2); border-radius: 14px;">
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div class="d-flex align-items-center gap-3">
            <div class="p-3 rounded-circle bg-warning-soft text-warning d-flex align-items-center justify-content-center" style="width: 52px; height: 52px; background: rgba(245, 158, 11, 0.12); border: 1px solid rgba(245, 158, 11, 0.3);">
              <i class="mdi mdi-shield-crown font-size-24" style="font-size: 26px;"></i>
            </div>
            <div>
              <h5 class="text-white font-weight-bold mb-1">
                {{ $currentLocale === 'en' ? 'Game Maker Operational Privileges' : 'Wewenang & Kuasa Game Maker' }}
              </h5>
              <p class="text-muted small mb-0" style="max-width: 750px;">
                {{ $currentLocale === 'en' 
                  ? 'Game Makers possess squad supervisor and gamification authoring access. They can craft quests, review proof submissions, configure team sanctions, and monitor anti-cheat telemetry.'
                  : 'Game Maker memiliki akses pengawasan squad dan perancangan gamifikasi. Maker dapat membuat quest/target, memverifikasi bukti pengajuan karyawan, menetapkan sanksi tim, dan memantau anti-cheat.' 
                }}
              </p>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <span class="badge badge-outline-warning px-3 py-2">
              <i class="mdi mdi-lock-check mr-1"></i> {{ $currentLocale === 'id' ? 'Peran: Pembuat Misi / Supervisor' : 'Role: Maker / Supervisor' }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Table Card -->
    <div class="col-12 grid-margin">
      <div class="card" style="background: #111420; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px;">
        <div class="card-body">
          <!-- Search & Filter Bar -->
          <div class="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
            <div class="d-flex align-items-center gap-2 flex-wrap" style="flex: 1; max-width: 720px;">
              <div class="search-box" style="min-width: 260px; flex: 1;">
                <div class="input-group">
                  <span class="input-group-text bg-dark border-secondary text-muted">
                    <i class="mdi mdi-magnify"></i>
                  </span>
                  <input
                    v-model.trim="search"
                    type="text"
                    class="form-control bg-dark border-secondary text-white"
                    :placeholder="$t('common.search') + ($currentLocale === 'id' ? ' maker, email, squad, jabatan...' : ' maker, email, squad, position...')"
                  />
                  <button v-if="search" class="btn btn-dark border-secondary text-muted" type="button" @click="search = ''">
                    <i class="mdi mdi-close"></i>
                  </button>
                </div>
              </div>

              <!-- Squad Filter -->
              <select
                v-model="squadFilter"
                class="form-select form-control bg-dark border-secondary text-white"
                style="max-width: 200px;"
              >
                <option value="all">{{ $t('common.all') }} {{ $t('player.squad') }}</option>
                <option v-for="sq in squads" :key="sq.id" :value="sq.id">
                  {{ sq.name }}
                </option>
              </select>
            </div>

            <div class="text-muted small">
              {{ $t('common.all') }}: <strong class="text-white">{{ filteredItems.length }}</strong>
            </div>
          </div>

          <!-- Table -->
          <div class="table-responsive">
            <table class="table table-hover text-white align-middle" style="width: 100%; table-layout: fixed;">
              <thead>
                <tr class="text-muted text-uppercase small" style="border-bottom: 1px solid rgba(255,255,255,0.1);">
                  <th style="width: 40px;">No</th>
                  <th style="width: 260px;">{{ $t('common.name') }}</th>
                  <th style="width: 180px;">{{ $t('player.squad') }}</th>
                  <th style="width: 160px;">{{ $t('common.role') }}</th>
                  <th style="width: 130px;">{{ $t('superadmin.joinDate') }}</th>
                  <th class="text-end text-right actions-cell" style="width: 170px; white-space: nowrap;">{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(data, index) in paginatedMaker"
                  :key="data.id"
                  style="border-bottom: 1px solid rgba(255,255,255,0.05);"
                >
                  <td class="text-muted">{{ index + 1 + (currentPage - 1) * itemsPerPage }}</td>
                  
                  <!-- Maker Name & Info -->
                  <td>
                    <div class="d-flex align-items-center">
                      <div
                        class="rounded-circle me-3 mr-3 d-flex align-items-center justify-content-center bg-dark text-warning font-weight-bold"
                        style="width: 38px; height: 38px; min-width: 38px; border: 1px solid rgba(255, 171, 0, 0.4);"
                      >
                        {{ (data.name || 'M').charAt(0).toUpperCase() }}
                      </div>
                      <div style="min-width: 0; overflow: hidden;">
                        <router-link
                          :to="{ name: 'DetailMaker', params: { id: data.id } }"
                          class="font-weight-bold text-white text-decoration-none text-truncate d-block"
                        >
                          {{ data.name || '-' }}
                        </router-link>
                        <div class="text-muted small text-truncate" style="font-size: 11px;">
                          {{ data.email || '-' }}
                        </div>
                        <div v-if="data.position" class="mt-1">
                          <span class="badge badge-outline-warning px-1 py-0 text-truncate d-inline-block" style="font-size: 10px; max-width: 180px;">
                            {{ data.position }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>

                  <!-- Squad / Division -->
                  <td>
                    <span v-if="data.division" class="badge badge-outline-info px-2 py-1 text-truncate d-inline-block" style="max-width: 170px;" :title="data.division.name">
                      <i class="mdi mdi-account-group mr-1"></i>
                      {{ data.division.name }}
                    </span>
                    <span v-else class="text-muted small">-</span>
                  </td>

                  <!-- Role Status -->
                  <td>
                    <div class="d-flex align-items-center gap-1 flex-wrap">
                      <span class="badge badge-warning text-dark font-weight-bold px-2 py-1" style="font-size: 11px;">
                        <i class="mdi mdi-shield-account mr-1"></i> Maker
                      </span>
                      <span v-if="hasPlayerRole(data.role)" class="badge badge-info px-2 py-1" style="font-size: 11px;">
                        +Player
                      </span>
                    </div>
                  </td>

                  <!-- Join Date -->
                  <td class="text-muted small" style="white-space: nowrap;">
                    {{ formatDate(data.created_at) }}
                  </td>

                  <!-- Actions Suite -->
                  <td class="text-end text-right actions-cell" style="white-space: nowrap; width: 170px;">
                    <div class="d-inline-flex align-items-center justify-content-end gap-1">
                      <!-- Quick Edit Modal -->
                      <button
                        @click="openEditModal(data)"
                        class="btn btn-outline-info btn-action-icon"
                        :title="$t('maker.editMaker')"
                      >
                        <i class="mdi mdi-pencil"></i>
                      </button>

                      <!-- Reset Password -->
                      <button
                        @click="openResetPasswordModal(data)"
                        class="btn btn-outline-warning btn-action-icon"
                        :title="$t('player.resetPassword')"
                      >
                        <i class="mdi mdi-key-variant"></i>
                      </button>

                      <!-- Player Role Toggle -->
                      <button
                        v-if="!hasPlayerRole(data.role)"
                        @click="handleAddToPlayer(data.id)"
                        class="btn btn-outline-success btn-action-icon"
                        :title="$t('maker.addToPlayer')"
                      >
                        <i class="mdi mdi-account-plus"></i>
                      </button>
                      <button
                        v-else
                        @click="handleRemoveFromPlayer(data.id)"
                        class="btn btn-outline-secondary btn-action-icon"
                        :title="$t('maker.confirmDemoteBtn')"
                      >
                        <i class="mdi mdi-account-minus"></i>
                      </button>

                      <!-- Delete Maker -->
                      <button
                        @click="confirmDelete(data)"
                        class="btn btn-outline-danger btn-action-icon"
                        :title="$t('maker.confirmDeleteBtn')"
                      >
                        <i class="mdi mdi-delete-outline"></i>
                      </button>
                    </div>
                  </td>
                </tr>

                <tr v-if="paginatedMaker.length === 0">
                  <td colspan="6" class="text-center py-5">
                    <EmptyState
                      icon="mdi-account-star-outline"
                      :title="$t('maker.noMakersFound')"
                      :description="$t('maker.noMakersDesc')"
                      :actionText="$t('maker.insertNew')"
                      actionTo="/maker/add"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="filteredItems.length > itemsPerPage" class="mt-4">
            <PaginationComponents
              :totalItems="filteredItems.length"
              :itemsPerPage="itemsPerPage"
              :currentPage="currentPage"
              @page-changed="onPageChanged"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Maker Modal -->
    <div
      v-if="isEditModalOpen"
      class="custom-modal-backdrop d-flex align-items-center justify-content-center"
      style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.85); z-index: 1050; backdrop-filter: blur(8px);"
    >
      <div class="card card-glass text-white" style="width: 520px; max-width: 95%; background: #141724; border: 1px solid rgba(255,255,255,0.12); border-radius: 14px;">
        <div class="card-header border-secondary d-flex justify-content-between align-items-center p-3">
          <h5 class="modal-title font-weight-bold text-white mb-0 d-flex align-items-center gap-2">
            <i class="mdi mdi-shield-edit text-warning"></i>
            <span>{{ $t('maker.editMaker') }}</span>
          </h5>
          <button type="button" class="close text-white" style="opacity:0.85;font-size:1.5rem;background:none;border:none;" @click="closeEditModal">&times;</button>
        </div>
        <div class="card-body p-4">
          <form @submit.prevent="submitEdit">
            <div class="mb-3">
              <label class="form-label text-muted small text-uppercase font-weight-bold">{{ $t('common.name') }} <span class="text-danger">*</span></label>
              <input
                type="text"
                v-model="editForm.name"
                class="form-control bg-dark border-secondary text-white"
                required
              />
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label text-muted small text-uppercase font-weight-bold">{{ $t('auth.emailLabel') }} <span class="text-danger">*</span></label>
                <input
                  type="email"
                  v-model="editForm.email"
                  class="form-control bg-dark border-secondary text-white"
                  required
                />
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label text-muted small text-uppercase font-weight-bold">{{ $t('playerForm.phoneNumber') }}</label>
                <input
                  type="text"
                  v-model="editForm.phone_number"
                  class="form-control bg-dark border-secondary text-white"
                  placeholder="08123456789"
                />
              </div>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label text-muted small text-uppercase font-weight-bold">{{ $t('player.position') }}</label>
                <input
                  type="text"
                  v-model="editForm.position"
                  class="form-control bg-dark border-secondary text-white"
                  :placeholder="$currentLocale === 'id' ? 'Contoh: Ketua Tim Desain' : 'e.g. Lead Designer'"
                />
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label text-muted small text-uppercase font-weight-bold">{{ $t('player.squad') }}</label>
                <select
                  v-model="editForm.division_id"
                  class="form-select form-control bg-dark border-secondary text-white"
                >
                  <option :value="null">-- {{ $t('playerForm.selectSquad') }} --</option>
                  <option v-for="sq in squads" :key="sq.id" :value="sq.id">
                    {{ sq.name }}
                  </option>
                </select>
              </div>
            </div>

            <div class="d-flex justify-content-end gap-2 mt-4">
              <button type="button" class="btn btn-secondary px-3" @click="closeEditModal">
                {{ $t('common.cancel') }}
              </button>
              <button type="submit" class="btn btn-warning text-dark font-weight-bold px-4" :disabled="editLoading">
                <span v-if="editLoading" class="spinner-border spinner-border-sm me-1"></span>
                {{ $t('common.save') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Reset Password Modal -->
    <div
      v-if="isResetModalOpen"
      class="custom-modal-backdrop d-flex align-items-center justify-content-center"
      style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.85); z-index: 1050; backdrop-filter: blur(8px);"
    >
      <div class="card card-glass text-white" style="width: 460px; max-width: 95%; background: #141724; border: 1px solid rgba(255,255,255,0.12); border-radius: 14px;">
        <div class="card-header border-secondary d-flex justify-content-between align-items-center p-3">
          <h5 class="modal-title font-weight-bold text-white mb-0 d-flex align-items-center gap-2">
            <i class="mdi mdi-key-variant text-warning"></i>
            <span>{{ $t('player.resetPasswordTitle') }}</span>
          </h5>
          <button type="button" class="close text-white" style="opacity:0.85;font-size:1.5rem;background:none;border:none;" @click="closeResetModal">&times;</button>
        </div>
        <div class="card-body p-4">
          <p class="text-muted small mb-3">
            {{ $t('player.resetPasswordSub') }} <strong class="text-white">{{ activeMaker?.name }}</strong> ({{ activeMaker?.email }})
          </p>

          <form @submit.prevent="submitResetPassword">
            <div class="mb-3">
              <label class="form-label text-muted small text-uppercase font-weight-bold">{{ $t('player.newPassword') }} <span class="text-danger">*</span></label>
              <div class="input-group">
                <input
                  :type="showNewPassword ? 'text' : 'password'"
                  v-model="newPassword"
                  class="form-control bg-dark border-secondary text-white"
                  :placeholder="$currentLocale === 'id' ? 'Minimal 6 karakter' : 'Min. 6 characters'"
                  required
                  minlength="6"
                />
                <button
                  type="button"
                  class="btn btn-dark border-secondary text-muted"
                  @click="showNewPassword = !showNewPassword"
                >
                  <i :class="showNewPassword ? 'mdi mdi-eye-off text-warning' : 'mdi mdi-eye'"></i>
                </button>
              </div>
            </div>

            <div class="d-flex justify-content-between align-items-center mb-3">
              <button
                type="button"
                class="btn btn-outline-secondary btn-xs py-1"
                @click="generateRandomPassword"
              >
                <i class="mdi mdi-dice-5-outline mr-1"></i> {{ $currentLocale === 'id' ? 'Buat Password Otomatis' : 'Auto Generate Password' }}
              </button>
            </div>

            <div class="d-flex justify-content-end gap-2 mt-4">
              <button type="button" class="btn btn-secondary px-3" @click="closeResetModal">
                {{ $t('common.cancel') }}
              </button>
              <button type="submit" class="btn btn-warning text-dark font-weight-bold px-4" :disabled="resetLoading || !newPassword">
                <span v-if="resetLoading" class="spinner-border spinner-border-sm me-1"></span>
                {{ $t('player.resetPassword') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getMaker,
  addToPlayer,
  removeFromMaker,
  deleteMaker,
  updateMaker,
  getSquad,
} from '@/services/makerService';
import { removeFromPlayer } from '@/services/playerService';
import moment from 'moment';
import PaginationComponents from '@/components/PaginationComponents.vue';
import EmptyState from '@/components/EmptyState.vue';
import Swal from 'sweetalert2';

export default {
  name: 'ListMaker',
  components: {
    PaginationComponents,
    EmptyState,
  },
  data() {
    return {
      search: '',
      squadFilter: 'all',
      maker: [],
      squads: [],
      currentPage: 1,
      itemsPerPage: 10,
      loading: false,

      // Edit Modal State
      isEditModalOpen: false,
      editLoading: false,
      editForm: {
        id: null,
        name: '',
        email: '',
        phone_number: '',
        position: '',
        division_id: null,
      },

      // Reset Password State
      isResetModalOpen: false,
      resetLoading: false,
      activeMaker: null,
      newPassword: '',
      showNewPassword: false,
    };
  },
  computed: {
    filteredItems() {
      let result = this.maker;

      // Squad filter
      if (this.squadFilter !== 'all') {
        result = result.filter(item => item.division_id === this.squadFilter);
      }

      // Search query
      if (this.search) {
        const q = this.search.toLowerCase();
        result = result.filter((item) => {
          const name = (item.name || '').toLowerCase();
          const email = (item.email || '').toLowerCase();
          const comp = (item.company && (item.company.company_name || item.company.name) || '').toLowerCase();
          const div = (item.division && item.division.name || '').toLowerCase();
          const pos = (item.position || '').toLowerCase();
          return (
            name.includes(q) ||
            email.includes(q) ||
            comp.includes(q) ||
            div.includes(q) ||
            pos.includes(q)
          );
        });
      }

      return result;
    },
    paginatedMaker() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredItems.slice(start, end);
    },
  },
  watch: {
    search() {
      this.currentPage = 1;
    },
    squadFilter() {
      this.currentPage = 1;
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD MMM YYYY');
    },
    hasPlayerRole(role) {
      if (!role) return false;
      return role.includes('player');
    },
    async fetchData() {
      this.loading = true;
      try {
        const [makerRes, squadRes] = await Promise.all([
          getMaker(),
          getSquad().catch(() => ({ data: [] })),
        ]);
        this.maker = makerRes.data || [];
        this.squads = squadRes.data || [];
      } catch (error) {
        console.error('Error fetching makers:', error);
      } finally {
        this.loading = false;
      }
    },
    onPageChanged(page) {
      this.currentPage = page;
    },

    // Assign Player Role
    async handleAddToPlayer(id) {
      Swal.fire({
        title: this.$t('maker.confirmAssignTitle'),
        text: this.$t('maker.confirmAssignText'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#ffab00',
        cancelButtonColor: '#6c757d',
        confirmButtonText: this.$t('maker.confirmAssignBtn'),
        cancelButtonText: this.$t('common.cancel'),
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await addToPlayer(id);
            Swal.fire({
              icon: 'success',
              title: this.$t('common.success'),
              text: this.$currentLocale === 'id' ? 'Peran Player berhasil ditambahkan ke Game Maker.' : 'Player role successfully assigned to Game Maker.',
              confirmButtonColor: '#ffab00',
            });
            await this.fetchData();
          } catch (error) {
            console.error('Error adding to player:', error);
            const msg = error.response?.data?.message || (this.$currentLocale === 'id' ? 'Gagal menambahkan peran Player.' : 'Failed to assign player role.');
            Swal.fire(this.$t('common.error'), msg, 'error');
          }
        }
      });
    },

    // Remove Player Role
    async handleRemoveFromPlayer(id) {
      const confirm = await Swal.fire({
        title: this.$t('maker.confirmDemoteTitle'),
        text: this.$t('maker.confirmDemoteText'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ffab00',
        cancelButtonColor: '#6c757d',
        confirmButtonText: this.$t('maker.confirmDemoteBtn'),
        cancelButtonText: this.$t('common.cancel'),
      });
      if (!confirm.isConfirmed) return;
      try {
        await removeFromPlayer(id);
        Swal.fire({
          icon: 'success',
          title: this.$t('common.success'),
          text: this.$currentLocale === 'id' ? 'Peran Player dihapus. Pengguna tetap aktif sebagai Game Maker.' : 'Player role removed. User remains active as Game Maker only.',
          confirmButtonColor: '#ffab00',
        });
        await this.fetchData();
      } catch (error) {
        console.error('Error removing player role:', error);
        Swal.fire(this.$t('common.error'), this.$currentLocale === 'id' ? 'Gagal mencabut peran Player.' : 'Failed to revoke player role.', 'error');
      }
    },

    // Demote to Player Only
    async handleDemoteToPlayer(id) {
      Swal.fire({
        title: this.$t('maker.confirmDemoteTitle'),
        text: this.$t('maker.confirmDemoteText'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ffab00',
        cancelButtonColor: '#6c757d',
        confirmButtonText: this.$t('maker.confirmDemoteBtn'),
        cancelButtonText: this.$t('common.cancel'),
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await removeFromMaker(id);
            Swal.fire({
              icon: 'success',
              title: this.$t('common.success'),
              text: this.$currentLocale === 'id' ? 'Peran Game Maker dihapus. Pengguna kini menjadi Player.' : 'Game Maker role removed. User is now a player only.',
              confirmButtonColor: '#ffab00',
            });
            await this.fetchData();
          } catch (error) {
            console.error('Error removing maker role:', error);
            Swal.fire(this.$t('common.error'), this.$currentLocale === 'id' ? 'Gagal mencabut peran Maker.' : 'Failed to revoke maker role.', 'error');
          }
        }
      });
    },

    // Delete Maker
    confirmDelete(data) {
      Swal.fire({
        title: this.$t('maker.confirmDeleteTitle'),
        text: `${this.$t('maker.confirmDeleteText')} (${data.name})`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
        confirmButtonText: this.$t('maker.confirmDeleteBtn'),
        cancelButtonText: this.$t('common.cancel'),
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await deleteMaker(data.id);
            Swal.fire({
              icon: 'success',
              title: this.$t('common.success'),
              text: this.$t('makerForm.deletedSuccess'),
              confirmButtonColor: '#0090e7',
            });
            await this.fetchData();
          } catch (error) {
            console.error('Error deleting maker:', error);
            Swal.fire(this.$t('common.error'), this.$currentLocale === 'id' ? 'Gagal menghapus Game Maker.' : 'Failed to delete maker.', 'error');
          }
        }
      });
    },

    // Open Edit Modal
    openEditModal(data) {
      this.editForm = {
        id: data.id,
        name: data.name || '',
        email: data.email || '',
        phone_number: data.phone_number || '',
        position: data.position || '',
        division_id: data.division_id || null,
      };
      this.isEditModalOpen = true;
    },
    closeEditModal() {
      this.isEditModalOpen = false;
    },
    async submitEdit() {
      this.editLoading = true;
      try {
        await updateMaker(this.editForm.id, {
          name: this.editForm.name,
          email: this.editForm.email,
          phone_number: this.editForm.phone_number,
          position: this.editForm.position,
          division_id: this.editForm.division_id,
        });
        this.closeEditModal();
        Swal.fire({
          icon: 'success',
          title: this.$t('common.success'),
          text: this.$t('makerForm.updatedSuccess'),
          confirmButtonColor: '#ffab00',
        });
        await this.fetchData();
      } catch (error) {
        console.error('Error updating maker:', error);
        const msg = error.response?.data?.message || (this.$currentLocale === 'id' ? 'Gagal memperbarui profil Game Maker.' : 'Failed to update maker profile.');
        Swal.fire(this.$t('common.error'), msg, 'error');
      } finally {
        this.editLoading = false;
      }
    },

    // Reset Password Methods
    openResetPasswordModal(data) {
      this.activeMaker = data;
      this.newPassword = '';
      this.showNewPassword = false;
      this.isResetModalOpen = true;
    },
    closeResetModal() {
      this.isResetModalOpen = false;
      this.activeMaker = null;
      this.newPassword = '';
    },
    generateRandomPassword() {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
      let pass = '';
      for (let i = 0; i < 10; i++) {
        pass += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      this.newPassword = pass;
      this.showNewPassword = true;
    },
    async submitResetPassword() {
      if (!this.activeMaker || !this.newPassword) return;
      this.resetLoading = true;
      try {
        const makerName = this.activeMaker?.name || 'Maker';
        await updateMaker(this.activeMaker.id, {
          password: this.newPassword,
        });
        const savedPass = this.newPassword;
        const isId = this.$currentLocale === 'id';
        this.closeResetModal();
        Swal.fire({
          icon: 'success',
          title: this.$t('player.resetPasswordSuccess'),
          html: `<div class="text-start mt-2"><p>${isId ? 'Password baru untuk' : 'New password for'} <strong>${makerName}</strong>:</p><code class="p-2 d-block bg-dark text-warning rounded font-weight-bold" style="font-size:16px;">${savedPass}</code><small class="text-muted d-block mt-2">${isId ? 'Harap bagikan kredensial ini secara aman kepada anggota tim.' : 'Please share this credential securely with the team member.'}</small></div>`,
          confirmButtonColor: '#ffab00',
        });
      } catch (error) {
        console.error('Error resetting password:', error);
        const msg = error.response?.data?.message || (this.$currentLocale === 'id' ? 'Gagal mereset password.' : 'Failed to reset password.');
        Swal.fire(this.$t('common.error'), msg, 'error');
      } finally {
        this.resetLoading = false;
      }
    },
  },
  async mounted() {
    await this.fetchData();
  },
};
</script>

<style scoped>
.btn-action-icon {
  width: 28px !important;
  min-width: 28px !important;
  max-width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  margin: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 6px !important;
}

.actions-cell {
  padding-left: 4px !important;
  padding-right: 8px !important;
  width: 170px !important;
}
</style>

